import { useMemo } from 'react';
import {
  BUDGET,
  CATS,
  MISC_MILESTONES,
  NEWD,
  REHAB,
  UNITS,
  barColor,
  fmt,
  pct1,
  sevColors,
} from '@piaip/shared';
import { sx, sxc } from '../sx';
import { schemeStatus } from '../derive';
import { useApp } from '../state';
import { api } from '../api';
import {
  Bar,
  CARD,
  CARD_LIFT,
  Dial,
  Empty,
  HINT,
  LABEL,
  SERIF_H,
  StatTile,
  TD,
  TH,
} from '../components/ui';
import { ProgressCurve } from '../components/ProgressCurve';

export function Dashboard() {
  const { engine, snapshot, can, mutate } = useApp();

  const view = useMemo(() => {
    const status = schemeStatus(engine);
    const { cats, spent, fin, t, gap } = status;
    const comp = cats.comp;
    const statusColor = status.color;

    const pending = engine.sortedWeeks().filter((w) => w.status === 'submitted');
    const drafts = engine.sortedWeeks().filter((w) => w.status === 'draft' || w.status === 'returned');

    const kpis = [
      {
        label: 'Physical progress',
        value: `${pct1(cats.overall)}%`,
        sub: 'approved construction work',
        color: '#0d3b35',
      },
      {
        label: 'Financial progress',
        value: `${pct1(fin.worksPct)}%`,
        sub: `Rs ${fmt(fin.works, 1)} Mn paid on construction`,
        color: '#0d3b35',
      },
      {
        label: 'Time elapsed',
        value: `${pct1(t.pct)}%`,
        sub: `${t.remaining} days to deadline`,
        color: '#0d3b35',
      },
      {
        label: 'Progress vs time',
        value: `${gap >= 0 ? '+' : ''}${pct1(gap)}`,
        sub: 'percentage points',
        color: statusColor,
      },
      {
        label: 'Weeks approved',
        value: String(engine.appr().length),
        sub: `${pending.length} awaiting approval`,
        color: '#0d3b35',
      },
    ];

    const catRows = CATS.map((c) => ({
      name: c.n,
      weight: `${Math.round(c.w * 1000) / 10}%`,
      pct: cats[c.k],
      budget: fmt(c.budget, 1),
      spent: fmt(spent[c.k] || 0, 1),
      spentPct: pct1(((spent[c.k] || 0) / c.budget) * 100),
    }));

    const schematic = [
      {
        title: 'Construction of new drains',
        items: NEWD.map((d) => {
          const c = Math.min(d.ft, engine.cumDrain('drainsNew', d.k));
          const p = (c / d.ft) * 100;
          return { name: d.n, pct: p, qty: `${fmt(c)} / ${fmt(d.ft)} ft` };
        }),
      },
      {
        title: 'Rehabilitation of existing drains',
        items: REHAB.map((d) => {
          const c = Math.min(d.ft, engine.cumDrain('drainsRehab', d.k));
          const p = (c / d.ft) * 100;
          return { name: d.n, pct: p, qty: `${fmt(c)} / ${fmt(d.ft)} ft` };
        }),
      },
      {
        title: 'Structures',
        items: UNITS.filter((g) => g.cat === 'civil').map((g) => ({
          name: g.n,
          pct: comp[g.k],
          qty: `${Math.min(g.total, engine.cumStage(g.k, g.stages.length - 1))} of ${g.total} done`,
        })),
      },
      {
        title: 'Electrical & mechanical',
        items: UNITS.filter((g) => g.cat !== 'civil').map((g) => ({
          name: g.n,
          pct: comp[g.k],
          qty: `${Math.min(g.total, engine.cumStage(g.k, g.stages.length - 1))} of ${g.total} done`,
        })),
      },
    ];

    const catBars = CATS.map((c) => {
      const fp = ((spent[c.k] || 0) / c.budget) * 100;
      return {
        name: c.n.replace(' Work', ''),
        physH: `${Math.max(1.5, Math.min(100, cats[c.k]))}%`,
        finH: `${Math.max(1.5, Math.min(100, fp))}%`,
      };
    });

    const em = engine.expByMonth();
    const emMax = Math.max(1, ...em.map((x) => x.v));
    const expBars = em.map((x) => ({
      label: new Date(`${x.k}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'short' }),
      yr: `'${x.k.slice(2, 4)}`,
      h: `${Math.max(2, (x.v / emMax) * 100)}%`,
      val: fmt(x.v, 1),
    }));

    const openIssues = (snapshot.issues ?? []).filter((i) => i.status !== 'Resolved');

    return {
      cats,
      spent,
      fin,
      t,
      statusColor,
      statusBg: status.bg,
      statusBorder: status.border,
      statusHead: status.head,
      statusText: status.text,
      pending,
      drafts,
      kpis,
      catRows,
      schematic,
      catBars,
      expBars,
      openIssues,
    };
  }, [engine, snapshot]);

  const canMisc = can('payments');

  return (
    <div
      style={sx(
        'max-width:1180px;margin:0 auto;padding:20px 16px 60px;display:flex;flex-direction:column;gap:18px',
      )}
    >
      {view.pending.length > 0 || view.drafts.length > 0 ? (
        <div
          style={sx(
            'background:#fdf3e3;border:1px solid #f2e0bd;border-radius:8px;padding:12px 16px;font-size:13px;color:#7a5410;line-height:1.5',
          )}
        >
          {view.pending.length} week(s) submitted and awaiting PMU approval, {view.drafts.length}{' '}
          still in draft. Figures below count approved weeks only.
        </div>
      ) : null}

      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:12px')}>
        {view.kpis.map((k) => (
          <StatTile key={k.label} tile={k} className="pms-lift" />
        ))}
      </div>

      <div
        style={sxc(
          'border-radius:8px;padding:14px 16px;display:flex;gap:12px;align-items:flex-start',
          `background:${view.statusBg}`,
          `border:1px solid ${view.statusBorder}`,
        )}
      >
        <div
          style={sxc('width:6px;align-self:stretch;border-radius:99px;flex:none', `background:${view.statusColor}`)}
        />
        <div style={sx('display:flex;flex-direction:column;gap:4px')}>
          <div style={sxc('font-size:14px;font-weight:600', `color:${view.statusColor}`)}>
            {view.statusHead}
          </div>
          <div style={sx('font-size:13px;color:#33403d;line-height:1.55;max-width:820px')}>
            {view.statusText}
          </div>
        </div>
      </div>

      <div
        style={sx(
          'display:grid;grid-template-columns:repeat(auto-fit,minmax(330px,1fr));gap:18px;align-items:start',
        )}
      >
        <div className="pms-lift-card" style={sx(CARD_LIFT)}>
          <div style={sxc(SERIF_H, 'margin-bottom:2px')}>Progress curve</div>
          <div style={sxc(HINT, 'margin-bottom:14px')}>
            Physical work done, money paid on construction and time consumed, week by week.
          </div>
          <ProgressCurve engine={engine} />
        </div>

        <div className="pms-lift-card" style={sx(CARD_LIFT)}>
          <div style={sxc(SERIF_H, 'margin-bottom:2px')}>The three dials</div>
          <div style={sxc(HINT, 'margin-bottom:14px')}>Work, money and time on the same scale.</div>
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(110px,1fr));gap:12px')}>
            <Dial label="Work done" value={view.cats.overall} color="#0f5c52" />
            <Dial label="Money on works" value={view.fin.worksPct} color="#c9a227" />
            <Dial label="Time used" value={view.t.pct} color="#4b8ea8" />
          </div>
          <div
            style={sx(
              'margin-top:16px;padding-top:14px;border-top:1px solid #eef2f1;display:flex;flex-direction:column;gap:12px',
            )}
          >
            <div style={sx(LABEL)}>Work done against money spent</div>
            <div style={sx('display:flex;gap:14px;align-items:flex-end;height:112px')}>
              {view.catBars.map((b) => (
                <div
                  key={b.name}
                  style={sx(
                    'flex:1;display:flex;flex-direction:column;align-items:center;gap:6px;height:100%',
                  )}
                >
                  <div style={sx('flex:1;display:flex;align-items:flex-end;gap:5px;width:100%')}>
                    <div
                      title={`Work done — ${b.physH}`}
                      style={sxc(
                        'flex:1;border-radius:4px 4px 0 0;background-color:#0f5c52;background-image:linear-gradient(180deg,rgba(255,255,255,.35),rgba(255,255,255,0) 55%,rgba(0,0,0,.18));box-shadow:0 6px 10px -6px rgba(13,59,53,.6)',
                        `height:${b.physH}`,
                      )}
                    />
                    <div
                      title={`Money spent — ${b.finH}`}
                      style={sxc(
                        'flex:1;border-radius:4px 4px 0 0;background-color:#c9a227;background-image:linear-gradient(180deg,rgba(255,255,255,.4),rgba(255,255,255,0) 55%,rgba(0,0,0,.18));box-shadow:0 6px 10px -6px rgba(13,59,53,.5)',
                        `height:${b.finH}`,
                      )}
                    />
                  </div>
                  <div style={sx('font-size:11px;color:#5a6b68;text-align:center;line-height:1.25')}>
                    {b.name}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="pms-lift-card" style={sx(CARD_LIFT)}>
          <div style={sxc(SERIF_H, 'margin-bottom:2px')}>Money released by month</div>
          <div style={sxc(HINT, 'margin-bottom:16px')}>
            Net payments to contractors and other charges, Rs million.
          </div>
          {view.expBars.length ? (
            <div style={sx('display:flex;gap:8px;align-items:flex-end;height:170px;overflow-x:auto;padding-bottom:2px')}>
              {view.expBars.map((e, i) => (
                <div
                  key={i}
                  style={sx(
                    'min-width:38px;flex:1;display:flex;flex-direction:column;align-items:center;gap:6px;height:100%',
                  )}
                >
                  <div style={sx("font-size:10px;color:#5a6b68;font-family:'IBM Plex Mono',monospace")}>
                    {e.val}
                  </div>
                  <div style={sx('flex:1;width:100%;display:flex;align-items:flex-end')}>
                    <div
                      style={sxc(
                        'width:100%;border-radius:5px 5px 0 0;background-color:#14766a;background-image:linear-gradient(180deg,rgba(255,255,255,.4),rgba(255,255,255,0) 50%,rgba(0,0,0,.2));box-shadow:0 8px 14px -8px rgba(13,59,53,.7),1px 0 0 rgba(0,0,0,.08) inset',
                        `height:${e.h}`,
                      )}
                    />
                  </div>
                  <div style={sx('font-size:10.5px;color:#5a6b68;text-align:center;line-height:1.2')}>
                    {e.label}
                    <br />
                    <span style={sx('color:#95a3a0')}>{e.yr}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Empty>No payment has been recorded yet.</Empty>
          )}
        </div>
      </div>

      <div
        style={sx(
          'display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:18px;align-items:start',
        )}
      >
        <div
          style={sx(
            'background:#fff;border:1px solid #dbe3e0;border-radius:12px;padding:18px;box-shadow:0 1px 0 #fff inset,0 18px 32px -24px rgba(13,59,53,.45)',
          )}
        >
          <div style={sxc(SERIF_H, 'margin-bottom:4px')}>Progress by work category</div>
          <div style={sxc(HINT, 'margin-bottom:16px')}>
            Weighted as per the approved cost split. Miscellaneous items are tracked separately and
            excluded from physical progress.
          </div>
          <div style={sx('display:flex;flex-direction:column;gap:16px')}>
            {view.catRows.map((c) => (
              <div key={c.name} style={sx('display:flex;flex-direction:column;gap:6px')}>
                <div
                  style={sx(
                    'display:flex;justify-content:space-between;align-items:baseline;gap:8px;flex-wrap:wrap',
                  )}
                >
                  <div style={sx('font-size:13px;font-weight:600')}>{c.name}</div>
                  <div style={sx('font-size:11px;color:#5a6b68')}>weight {c.weight}</div>
                  <div style={sx('flex:1')} />
                  <div style={sx("font-family:'IBM Plex Mono',monospace;font-size:15px")}>
                    {pct1(c.pct)}%
                  </div>
                </div>
                <Bar pct={c.pct} />
                <div style={sx('font-size:12px;color:#5a6b68')}>
                  Budget Rs {c.budget} Mn &nbsp;·&nbsp; Spent Rs {c.spent} Mn ({c.spentPct}%)
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={sx(CARD)}>
          <div style={sxc(SERIF_H, 'margin-bottom:16px')}>Budget utilisation</div>
          <div style={sx('display:flex;flex-direction:column;gap:12px')}>
            <Money label="Sanctioned cost" value={fmt(BUDGET, 1)} />
            <Money label="Expenditure to date" value={fmt(view.spent.total, 1)} />
            <Money label="· on construction work" value={fmt(view.fin.works, 1)} />
            <Money label="· on land, environment, contingencies & tax" value={fmt(view.spent.misc || 0, 1)} />
            <Money label="Balance available" value={fmt(BUDGET - view.spent.total, 1)} />
            <Bar pct={(view.spent.total / BUDGET) * 100} color="#c9a227" />
            <div style={sx('font-size:12px;color:#5a6b68;line-height:1.5')}>
              Payments are logged week by week under Weekly Entry and counted once the week is
              approved.
            </div>
            <div
              style={sx('border-top:1px solid #eef2f1;padding-top:12px;display:flex;flex-direction:column;gap:8px')}
            >
              <div style={sx(LABEL)}>Non-construction milestones</div>
              {MISC_MILESTONES.map((m) => (
                <div
                  key={m.k}
                  style={sx('display:flex;justify-content:space-between;align-items:center;gap:10px')}
                >
                  <label htmlFor={`misc-${m.k}`} style={sx('font-size:12.5px')}>
                    {m.n}
                  </label>
                  <input
                    id={`misc-${m.k}`}
                    type="number"
                    min={0}
                    max={100}
                    defaultValue={(snapshot.misc ?? {})[m.k] ?? ''}
                    key={`${m.k}-${(snapshot.misc ?? {})[m.k] ?? ''}`}
                    disabled={!canMisc}
                    onBlur={(e) => {
                      const raw = e.target.value;
                      if (raw === '') return;
                      void mutate(() => api.setMisc(m.k, raw));
                    }}
                    style={sx(
                      'width:78px;padding:5px 7px;border:1px solid #d3dcda;border-radius:4px;font-size:12.5px;text-align:right;box-sizing:border-box',
                    )}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={sx(CARD)}>
        <div style={sxc(SERIF_H, 'margin-bottom:4px')}>Scheme schematic</div>
        <div style={sxc(HINT, 'margin-bottom:18px')}>
          Every channel and every structure group in the scheme, shaded by how much of it is
          finished.
        </div>
        <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:24px')}>
          {view.schematic.map((s) => (
            <div key={s.title} style={sx('display:flex;flex-direction:column;gap:9px')}>
              <div
                style={sx(
                  'font-size:10.5px;letter-spacing:.08em;text-transform:uppercase;color:#0f5c52;font-weight:700;border-bottom:1px solid #dbe3e0;padding-bottom:6px',
                )}
              >
                {s.title}
              </div>
              {s.items.map((i) => (
                <div key={i.name} style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap')}>
                  <div style={sx('width:135px;font-size:12.5px;line-height:1.3;flex:none')}>{i.name}</div>
                  <Bar
                    pct={i.pct}
                    color={barColor(i.pct)}
                    height={14}
                    radius={3}
                    style={{ flex: 1, minWidth: '90px' }}
                  />
                  <div
                    style={sx(
                      "width:46px;text-align:right;font-family:'IBM Plex Mono',monospace;font-size:12px;flex:none",
                    )}
                  >
                    {pct1(i.pct)}%
                  </div>
                  <div style={sx('width:92px;font-size:11px;color:#5a6b68;text-align:right;flex:none')}>
                    {i.qty}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div style={sx(CARD)}>
        <div style={sxc(SERIF_H, 'margin-bottom:14px')}>Open issues requiring attention</div>
        {view.openIssues.length ? (
          <div style={sx('overflow-x:auto')}>
            <table style={sx('width:100%;border-collapse:collapse;min-width:520px')}>
              <thead>
                <tr>
                  {['Issue', 'Affects', 'Owner', 'Severity', 'Status'].map((h) => (
                    <th key={h} style={sx(TH)}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {view.openIssues.map((i) => {
                  const c = sevColors(i.severity);
                  return (
                    <tr key={i.id}>
                      <td style={sx(TD)}>{i.title || 'Untitled issue'}</td>
                      <td style={sxc(TD, 'color:#5a6b68')}>{i.component || '—'}</td>
                      <td style={sxc(TD, 'color:#5a6b68')}>{i.owner || '—'}</td>
                      <td style={sx('padding:9px 10px;border-bottom:1px solid #eef2f1;font-size:12px')}>
                        <span
                          style={sxc(
                            'padding:3px 8px;border-radius:99px;font-weight:600;white-space:nowrap',
                            `background:${c.bg}`,
                            `color:${c.fg}`,
                          )}
                        >
                          {i.severity || 'Minor'}
                        </span>
                      </td>
                      <td style={sxc(TD, 'color:#5a6b68')}>{i.status || 'Open'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={sx('font-size:13px;color:#5a6b68;padding:8px 0')}>
            No open issues recorded. Add them under the Issues tab so they appear in the delay
            report.
          </div>
        )}
      </div>
    </div>
  );
}

function Money({ label, value }: { label: string; value: string }) {
  return (
    <div style={sx('display:flex;justify-content:space-between;font-size:13px;gap:10px')}>
      <span style={sx('color:#5a6b68')}>{label}</span>
      <span style={sx("font-family:'IBM Plex Mono',monospace;white-space:nowrap")}>Rs {value} Mn</span>
    </div>
  );
}
